import { useState, useEffect, useCallback } from 'react';
import * as api from '@/services/api';
import { notifyAnalysisComplete } from '@/services/notifications';
import { isDoneStatus, isForwardStatus, isReworkStatus } from '@/services/jira-status-config';
import { withJiraExpiryCheck, type JiraCredentials } from '@/services/jira-token-expiry';
import { useJiraTokenExpiryModal } from '@/components/JiraTokenExpiryModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowTrendDown,
  faArrowTrendUp,
  faBolt,
  faBug,
  faBuilding,
  faBullseye,
  faCalendarDays,
  faChartBar,
  faChartLine,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faCodeCommit,
  faFire,
  faRotate,
  faShieldHalved,
  faSpinner,
  faTriangleExclamation,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;


// Types
interface LagIndicatorDevResult {
  developerName: string;
  developerAccountId: string;
  value: number;
  label: string;
  severity: 'good' | 'warning' | 'critical';
  details?: string;
  issueKeys?: string[];
}

interface LagIndicatorResult {
  id: number;
  name: string;
  description: string;
  scope?: 'per-user' | 'overall';
  results: LagIndicatorDevResult[];
  overallValue: number;
  overallLabel: string;
  severity: 'good' | 'warning' | 'critical';
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface ActivityEvent {
  id: string;
  timestamp: string;
  type: string;
  issueKey: string;
  issueSummary: string;
  issueType: string;
  author: { displayName: string; accountId: string; avatarUrl?: string };
  fromStatus?: string;
  toStatus?: string;
  fromAssignee?: string;
  toAssignee?: string;
}

interface SprintSummaryItem {
  name: string;
  state: string;
  totalIssues: number;
  completedIssues: number;
}

interface AggregateIndicator {
  id: string;
  name: string;
  label: string;
  severity: 'good' | 'warning' | 'critical';
  icon: any;
  baseline: string;
}

interface JiraLagIndicatorsProps {
  onClose: () => void;
  jiraDomain: string;
  jiraEmail: string;
  jiraApiToken: string;
  defaultProjectKey?: string;
  priorityUsers?: string[];
  embedded?: boolean;
  triggerCalculate?: number;
  parentLookbackDays?: number;
  parentStartDate?: string;
  parentEndDate?: string;
  activityEvents?: ActivityEvent[];
  activitySprintSummary?: SprintSummaryItem[];
}

const INDICATOR_ICONS: Record<number, React.ComponentType<{ className?: string }>> = {
  1: mkIcon(faRotate),    // Dev rework
  2: mkIcon(faCircleCheck), // Testing pass rate
  3: mkIcon(faBug),          // Bugs after completion
  4: mkIcon(faClock),        // Estimate overrun
  5: mkIcon(faCodeCommit),    // Cards without commit
  6: mkIcon(faFire),        // Hotfix without commit
  7: mkIcon(faChartLine),     // Cycle time
  8: mkIcon(faBullseye),       // Scope creep
  9: mkIcon(faTriangleExclamation),// Reopened rate
  10: mkIcon(faCircleCheck), // Issues completed (lead)
  11: mkIcon(faShieldHalved),       // First-pass rate (lead)
  12: mkIcon(faUsers),        // Issues touched (lead)
  13: mkIcon(faBolt),          // Fast cycle rate (lead)
  14: mkIcon(faArrowTrendUp),   // Story points delivered (lead)
  15: mkIcon(faBullseye),        // Estimation accuracy (lead)
  16: mkIcon(faBug),           // Bugs resolved (lead)
  17: mkIcon(faChartLine),      // Component breadth (lead)
  18: mkIcon(faClock),         // Consistent delivery (lead)
};

const SEVERITY_COLORS = {
  good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-400' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-400' },
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-400' },
};

export function JiraLagIndicators({ onClose, jiraDomain, jiraEmail, jiraApiToken, defaultProjectKey, priorityUsers = [], embedded, triggerCalculate, parentLookbackDays, parentStartDate, parentEndDate, activityEvents = [], activitySprintSummary = [] }: JiraLagIndicatorsProps) {

  // Token expiry modal
  const { Modal: TokenExpiryModal } = useJiraTokenExpiryModal();

  // State
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>(defaultProjectKey || '');
  const [lookbackDays, setLookbackDays] = useState(30);
  const [indicators, setIndicators] = useState<LagIndicatorResult[]>([]);
  const [leadIndicators, setLeadIndicators] = useState<LagIndicatorResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIndicators, setExpandedIndicators] = useState<Set<number>>(new Set());
  const [lastCalculated, setLastCalculated] = useState<Date | null>(null);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [indicatorsCollapsed, setIndicatorsCollapsed] = useState(true);

  // Sync project and lookback from parent when they change
  useEffect(() => {
    if (defaultProjectKey) setSelectedProject(defaultProjectKey);
  }, [defaultProjectKey]);

  useEffect(() => {
    if (parentLookbackDays) setLookbackDays(parentLookbackDays);
  }, [parentLookbackDays]);

  // Auto-calculate when parent triggers it
  useEffect(() => {
    if (triggerCalculate && triggerCalculate > 0 && selectedProject) {
      calculateIndicators();
    }
  }, [triggerCalculate]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken };
      const result = await withJiraExpiryCheck(settings, () =>
        api.getJiraProjects(jiraDomain, jiraEmail, jiraApiToken)
      );

      if (result.error) {
        console.error('Jira authentication error:', result.error);
        return;
      }

      const data = result.result;
      if (data) {
        if (Array.isArray(data)) setProjects(data);
        else if (data && typeof data === 'object' && 'projects' in data) {
          setProjects((data as any).projects);
        }
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const calculateIndicators = useCallback(async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    setLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken };
      
      // Get lag indicators with expiry detection
      const lagResult = await withJiraExpiryCheck(settings, () =>
        api.getJiraLagIndicators(
          jiraDomain, jiraEmail, jiraApiToken, selectedProject, [], lookbackDays, parentStartDate, parentEndDate
        )
      );

      if (lagResult.error) {
        setError(lagResult.error.message);
        notifyAnalysisComplete({ mode: 'indicators', commitCount: 0, eventCount: 0, durationMs: Date.now() - startTime, success: false, error: lagResult.error.message });
        return;
      }

      const data = lagResult.result;
      if (data && 'indicators' in data) {
        setIndicators(data.indicators);
        setLastCalculated(new Date());
        notifyAnalysisComplete({ mode: 'indicators', commitCount: 0, eventCount: data.indicators.length, durationMs: Date.now() - startTime, success: true });
      }

      // Also fetch lead indicators (non-critical)
      try {
        const leadResult = await withJiraExpiryCheck(settings, () =>
          api.getJiraLeadIndicators(
            jiraDomain, jiraEmail, jiraApiToken, selectedProject, [], lookbackDays, parentStartDate, parentEndDate
          )
        );
        if (leadResult.result?.indicators) setLeadIndicators(leadResult.result.indicators);
      } catch { /* lead indicators are non-critical */ }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate indicators');
      notifyAnalysisComplete({ mode: 'indicators', commitCount: 0, eventCount: 0, durationMs: Date.now() - startTime, success: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }, [selectedProject, lookbackDays, jiraDomain, jiraEmail, jiraApiToken, parentStartDate, parentEndDate]);

  const toggleIndicator = (id: number) => {
    setExpandedIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Keyboard: ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Filter per-user vs overall indicators
  const perUserLagIndicators = indicators.filter(i => (i.scope || 'per-user') === 'per-user');
  const perUserLeadIndicators = leadIndicators.filter(i => (i.scope || 'per-user') === 'per-user');

  // Summary stats — per-user lag only
  const goodCount = perUserLagIndicators.filter((i) => i.severity === 'good').length;
  const warningCount = perUserLagIndicators.filter((i) => i.severity === 'warning').length;
  const criticalCount = perUserLagIndicators.filter((i) => i.severity === 'critical').length;

  // Aggregated counts across ALL indicator sources for collapsed header
  const leadGoodCount = perUserLeadIndicators.filter(i => i.severity === 'good').length;
  const leadWarnCount = perUserLeadIndicators.filter(i => i.severity === 'warning').length;
  const leadCritCount = perUserLeadIndicators.filter(i => i.severity === 'critical').length;

  // Compute aggregate indicator counts from activity events
  const aggCounts = (() => {
    if (activityEvents.length === 0) return { good: 0, warning: 0, critical: 0 };
    const events = activityEvents;
    const sprints = activitySprintSummary;
    let good = 0, warn = 0, crit = 0;
    const count = (severity: string) => { if (severity === 'good') good++; else if (severity === 'warning') warn++; else crit++; };

    // Lead agg: throughput
    const weekMap = new Map<string, Set<string>>();
    for (const e of events) { const d = new Date(e.timestamp); const wk = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`; if (!weekMap.has(wk)) weekMap.set(wk, new Set()); weekMap.get(wk)!.add(e.issueKey); }
    const wAvg = weekMap.size > 0 ? [...weekMap.values()].reduce((s, v) => s + v.size, 0) / weekMap.size : 0;
    count(wAvg >= 15 ? 'good' : wAvg >= 8 ? 'warning' : 'critical');
    // Lead agg: sprint completion
    const aSprints = sprints.filter(s => s.state === 'active' || s.state === 'closed');
    if (aSprints.length > 0) { const tc = aSprints.reduce((s, v) => s + v.completedIssues, 0); const tt = aSprints.reduce((s, v) => s + v.totalIssues, 0); const cr = tt > 0 ? (tc / tt) * 100 : 0; count(cr >= 80 ? 'good' : cr >= 60 ? 'warning' : 'critical'); }
    // Lead agg: active team
    const teamSize = new Set(events.map(e => e.author.displayName)).size;
    count(teamSize >= 5 ? 'good' : teamSize >= 3 ? 'warning' : 'critical');
    // Lead agg: resolved
    const resolved = new Set(events.filter(e => e.type === 'status_change' && e.toStatus && isDoneStatus(e.toStatus)).map(e => e.issueKey));
    count(resolved.size >= 20 ? 'good' : resolved.size >= 8 ? 'warning' : 'critical');
    // Lead agg: high priority resolution
    const hp = new Set(events.filter(e => e.issueType.toLowerCase() === 'bug' || e.issueType.toLowerCase() === 'hotfix').map(e => e.issueKey));
    const hpRes = new Set([...resolved].filter(k => hp.has(k)));
    const hpPct = hp.size > 0 ? (hpRes.size / hp.size) * 100 : 0;
    count(hp.size === 0 ? 'good' : hpPct >= 80 ? 'good' : hpPct >= 50 ? 'warning' : 'critical');
    // Lag agg: thrashing
    const sc = new Map<string, number>(); for (const e of events) { if (e.type === 'status_change') sc.set(e.issueKey, (sc.get(e.issueKey) || 0) + 1); }
    const thrashPct = sc.size > 0 ? ([...sc.entries()].filter(([, c]) => c > 3).length / sc.size) * 100 : 0;
    count(thrashPct > 30 ? 'critical' : thrashPct > 15 ? 'warning' : 'good');
    // Lag agg: rework
    let rwk = 0; const ilf = new Map<string, boolean>(); const sorted = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    for (const e of sorted) { if (e.type === 'status_change' && e.toStatus) { if (isForwardStatus(e.toStatus)) ilf.set(e.issueKey, true); else if (isReworkStatus(e.toStatus) && ilf.get(e.issueKey)) { rwk++; ilf.set(e.issueKey, false); } } }
    const uiq = new Set(events.map(e => e.issueKey)).size; const rwkPct = uiq > 0 ? (rwk / uiq) * 100 : 0;
    count(rwkPct > 25 ? 'critical' : rwkPct > 10 ? 'warning' : 'good');
    // Lag agg: unassignment
    const ua = events.filter(e => e.type === 'assignment' && !e.toAssignee).length;
    count(ua > 10 ? 'critical' : ua > 3 ? 'warning' : 'good');
    // Lag agg: sprint incompletion
    if (aSprints.length > 0) { const tc2 = aSprints.reduce((s, v) => s + v.completedIssues, 0); const tt2 = aSprints.reduce((s, v) => s + v.totalIssues, 0); const ic = tt2 > 0 ? ((tt2 - tc2) / tt2) * 100 : 0; count(ic > 40 ? 'critical' : ic > 20 ? 'warning' : 'good'); }
    // Lag agg: stale
    const ila = new Map<string, number>(); const ils = new Map<string, string>();
    for (const e of events) { const ts = new Date(e.timestamp).getTime(); if (!ila.has(e.issueKey) || ts > ila.get(e.issueKey)!) ila.set(e.issueKey, ts); if (e.type === 'status_change' && e.toStatus && ts >= (ila.get(e.issueKey) || 0)) ils.set(e.issueKey, e.toStatus.toLowerCase()); }
    const openI = [...ila.entries()].filter(([key]) => { const ls = ils.get(key) || ''; return !isDoneStatus(ls); });
    const staleI = openI.filter(([, ts]) => (Date.now() - ts) > 7 * 86400000);
    const stalePct = openI.length > 0 ? (staleI.length / openI.length) * 100 : 0;
    count(stalePct > 40 ? 'critical' : stalePct > 20 ? 'warning' : 'good');

    return { good, warning: warn, critical: crit };
  })();

  const totalGood = goodCount + leadGoodCount + aggCounts.good;
  const totalWarning = warningCount + leadWarnCount + aggCounts.warning;
  const totalCritical = criticalCount + leadCritCount + aggCounts.critical;

  // Embedded mode: compact rendering inside parent view
  if (embedded) {
    return (
      <div className="px-4 py-3 space-y-3">
        {/* Compact header with calculate button and collapse toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIndicatorsCollapsed(!indicatorsCollapsed)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {indicatorsCollapsed ? <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-slate-500" /> : <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-slate-500" />}
            <FontAwesomeIcon icon={faChartBar} className="h-4 w-4 text-indigo-400" />
            <span className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
              {indicatorsCollapsed ? 'Health Indicators' : 'Per User Lag Indicators'}
            </span>
            {lastCalculated && <span className="text-[10px] text-slate-600">({lastCalculated.toLocaleTimeString()})</span>}
            {indicatorsCollapsed ? (
              (totalGood > 0 || totalWarning > 0 || totalCritical > 0) && (
                <div className="flex items-center gap-1.5 ml-2">
                  {totalGood > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">{totalGood} healthy</span>}
                  {totalWarning > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/20">{totalWarning} warning</span>}
                  {totalCritical > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/20">{totalCritical} critical</span>}
                </div>
              )
            ) : (
              perUserLagIndicators.length > 0 && (
                <div className="flex items-center gap-1.5 ml-2">
                  {goodCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">{goodCount} healthy</span>}
                  {warningCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/20">{warningCount} warning</span>}
                  {criticalCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/20">{criticalCount} critical</span>}
                </div>
              )
            )}
          </button>
          <button
            onClick={calculateIndicators}
            disabled={loading || !selectedProject}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
          >
            {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Calculating...</> : <><FontAwesomeIcon icon={faBolt} className="h-3 w-3" /> Calculate</>}
          </button>
        </div>

        {error && <div className="text-xs text-red-400 px-2">{error}</div>}

        {/* Compact results table */}
        {!indicatorsCollapsed && perUserLagIndicators.length > 0 && (() => {
          const devActivity = new Map<string, number>();
          for (const ind of perUserLagIndicators) {
            for (const r of ind.results) {
              devActivity.set(r.developerName, (devActivity.get(r.developerName) || 0) + Math.abs(r.value));
            }
          }
          const allDevNamesSorted = Array.from(devActivity.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name);
          const prioritySet = new Set(priorityUsers.map(n => n.toLowerCase()));
          const priorityMatched = priorityUsers.map(pu => allDevNamesSorted.find(d => d.toLowerCase() === pu.toLowerCase())).filter(Boolean) as string[];
          const rest = allDevNamesSorted.filter(d => !prioritySet.has(d.toLowerCase()));
          const allDevNames = [...priorityMatched, ...rest];
          const visibleDevs = showAllUsers ? allDevNames : allDevNames.slice(0, 6);
          const hiddenCount = allDevNames.length - 6;
          const getDevResult = (indicator: LagIndicatorResult, devName: string) => indicator.results.find((r) => r.developerName === devName);

          return (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700/30">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-900/50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400 min-w-[140px]">Indicator</th>
                    {visibleDevs.map((name) => (
                      <th key={name} className="text-center px-2 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400 min-w-[60px] capitalize">{name.split(' ')[0]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perUserLagIndicators.map((indicator) => {
                    const Icon = INDICATOR_ICONS[indicator.id] || mkIcon(faChartBar);
                    return (
                      <tr key={indicator.id} className="border-t border-gray-100 dark:border-slate-700/15 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                            <span className="text-[11px] text-gray-700 dark:text-slate-300 truncate">{indicator.name}</span>
                          </div>
                        </td>
                        {visibleDevs.map((name) => {
                          const result = getDevResult(indicator, name);
                          if (!result) return <td key={name} className="px-2 py-1.5 text-center text-slate-700">—</td>;
                          const colors = SEVERITY_COLORS[result.severity];
                          return (
                            <td key={name} className="px-2 py-1.5 text-center">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>{result.label}</span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hiddenCount > 0 && (
                <div className="border-t border-gray-200 dark:border-slate-700/20 px-3 py-1.5 text-center">
                  <button onClick={() => setShowAllUsers(!showAllUsers)} className="text-[10px] text-blue-400 hover:text-blue-300">
                    {showAllUsers ? 'Show fewer' : `+${hiddenCount} more users`}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Per-user LEAD indicators table */}
        {!indicatorsCollapsed && perUserLeadIndicators.length > 0 && (() => {
          const devActivity = new Map<string, number>();
          for (const ind of perUserLeadIndicators) {
            for (const r of ind.results) {
              devActivity.set(r.developerName, (devActivity.get(r.developerName) || 0) + Math.abs(r.value));
            }
          }
          const allDevNamesSorted = Array.from(devActivity.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name);
          const prioritySet = new Set(priorityUsers.map(n => n.toLowerCase()));
          const priorityMatched = priorityUsers.map(pu => allDevNamesSorted.find(d => d.toLowerCase() === pu.toLowerCase())).filter(Boolean) as string[];
          const rest = allDevNamesSorted.filter(d => !prioritySet.has(d.toLowerCase()));
          const allDevNames = [...priorityMatched, ...rest];
          const visibleDevs = showAllUsers ? allDevNames : allDevNames.slice(0, 6);
          const hiddenCount = allDevNames.length - 6;
          const getDevResult = (indicator: LagIndicatorResult, devName: string) => indicator.results.find((r) => r.developerName === devName);

          const leadGood = perUserLeadIndicators.filter(i => i.severity === 'good').length;
          const leadWarn = perUserLeadIndicators.filter(i => i.severity === 'warning').length;
          const leadCrit = perUserLeadIndicators.filter(i => i.severity === 'critical').length;

          return (
            <div className="space-y-1.5 mt-3">
              <div className="flex items-center gap-2 px-1">
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Per User Lead Indicators</span>
                <div className="flex items-center gap-1 ml-auto">
                  {leadGood > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">{leadGood} ✓</span>}
                  {leadWarn > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/20">{leadWarn} ⚠</span>}
                  {leadCrit > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/20">{leadCrit} ✗</span>}
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700/30">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-900/50">
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400 min-w-[140px]">Indicator</th>
                      {visibleDevs.map((name) => (
                        <th key={name} className="text-center px-2 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400 min-w-[60px] capitalize">{name.split(' ')[0]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perUserLeadIndicators.map((indicator) => {
                      const Icon = INDICATOR_ICONS[indicator.id] || mkIcon(faArrowTrendUp);
                      return (
                        <tr key={indicator.id} className="border-t border-gray-100 dark:border-slate-700/15 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <Icon className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-slate-300 truncate">{indicator.name}</span>
                            </div>
                          </td>
                          {visibleDevs.map((name) => {
                            const result = getDevResult(indicator, name);
                            if (!result) return <td key={name} className="px-2 py-1.5 text-center text-slate-700">—</td>;
                            const colors = SEVERITY_COLORS[result.severity];
                            return (
                              <td key={name} className="px-2 py-1.5 text-center">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>{result.label}</span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {hiddenCount > 0 && (
                  <div className="border-t border-gray-200 dark:border-slate-700/20 px-3 py-1.5 text-center">
                    <button onClick={() => setShowAllUsers(!showAllUsers)} className="text-[10px] text-blue-400 hover:text-blue-300">
                      {showAllUsers ? 'Show fewer' : `+${hiddenCount} more users`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {!indicatorsCollapsed && loading && (
          <div className="flex items-center justify-center gap-2 py-4">
            <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 text-blue-400 animate-spin" />
            <span className="text-xs text-gray-500 dark:text-slate-400">Calculating per user indicators...</span>
          </div>
        )}

        {/* Aggregate Jira Lead / Lag tables (computed from activity events) */}
        {!indicatorsCollapsed && activityEvents.length > 0 && (() => {
          const events = activityEvents;
          const sprints = activitySprintSummary;

          // Jira Lead Indicators
          const jiraLead: AggregateIndicator[] = [];
          // Throughput
          const weekMap = new Map<string, Set<string>>();
          for (const e of events) {
            const d = new Date(e.timestamp);
            const wk = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`;
            if (!weekMap.has(wk)) weekMap.set(wk, new Set());
            weekMap.get(wk)!.add(e.issueKey);
          }
          const wAvg = weekMap.size > 0 ? [...weekMap.values()].reduce((s, v) => s + v.size, 0) / weekMap.size : 0;
          jiraLead.push({ id: 'jl-throughput', name: 'Weekly Throughput', label: `${wAvg.toFixed(1)} issues/week`, severity: wAvg >= 15 ? 'good' : wAvg >= 8 ? 'warning' : 'critical', icon: mkIcon(faChartBar), baseline: '✓ ≥15/wk | ⚠ 8–15 | ✗ <8' });
          // Sprint completion
          const aSprints = sprints.filter(s => s.state === 'active' || s.state === 'closed');
          if (aSprints.length > 0) {
            const tc = aSprints.reduce((s, v) => s + v.completedIssues, 0);
            const tt = aSprints.reduce((s, v) => s + v.totalIssues, 0);
            const cr = tt > 0 ? (tc / tt) * 100 : 0;
            jiraLead.push({ id: 'jl-sprint', name: 'Sprint Completion', label: `${cr.toFixed(0)}% (${tc}/${tt})`, severity: cr >= 80 ? 'good' : cr >= 60 ? 'warning' : 'critical', icon: mkIcon(faCircleCheck), baseline: '✓ ≥80% | ⚠ 60–80% | ✗ <60%' });
          }
          // Active team
          const teamSize = new Set(events.map(e => e.author.displayName)).size;
          jiraLead.push({ id: 'jl-team', name: 'Active Team Members', label: `${teamSize} member${teamSize !== 1 ? 's' : ''}`, severity: teamSize >= 5 ? 'good' : teamSize >= 3 ? 'warning' : 'critical', icon: mkIcon(faUsers), baseline: '✓ ≥5 | ⚠ 3–4 | ✗ <3' });
          // Resolved
          const resolved = new Set(events.filter(e => e.type === 'status_change' && e.toStatus && isDoneStatus(e.toStatus)).map(e => e.issueKey));
          jiraLead.push({ id: 'jl-resolved', name: 'Issues Resolved', label: `${resolved.size} resolved`, severity: resolved.size >= 20 ? 'good' : resolved.size >= 8 ? 'warning' : 'critical', icon: mkIcon(faBolt), baseline: '✓ ≥20 | ⚠ 8–20 | ✗ <8' });
          // High Priority Resolution
          const highPriorityTypes = events.filter(e => e.issueType.toLowerCase() === 'bug' || e.issueType.toLowerCase() === 'hotfix');
          const highPriorityKeys = new Set(highPriorityTypes.map(e => e.issueKey));
          const highResolved = new Set([...resolved].filter(k => highPriorityKeys.has(k)));
          const highResPct = highPriorityKeys.size > 0 ? (highResolved.size / highPriorityKeys.size) * 100 : 0;
          jiraLead.push({ id: 'jl-high-priority', name: 'High Priority Resolution', label: `${highResPct.toFixed(0)}% (${highResolved.size}/${highPriorityKeys.size})`, severity: highPriorityKeys.size === 0 ? 'good' : highResPct >= 80 ? 'good' : highResPct >= 50 ? 'warning' : 'critical', icon: mkIcon(faShieldHalved), baseline: '✓ ≥80% | ⚠ 50–80% | ✗ <50%' });

          // Jira Lag Indicators
          const jiraLag: AggregateIndicator[] = [];
          // Status thrashing
          const statusChanges = new Map<string, number>();
          for (const e of events) { if (e.type === 'status_change') statusChanges.set(e.issueKey, (statusChanges.get(e.issueKey) || 0) + 1); }
          const thrashing = [...statusChanges.entries()].filter(([, c]) => c > 3);
          const thrashPct = statusChanges.size > 0 ? (thrashing.length / statusChanges.size) * 100 : 0;
          jiraLag.push({ id: 'jlag-thrash', name: 'Status Thrashing', label: `${thrashing.length} issues (${thrashPct.toFixed(0)}%)`, severity: thrashPct > 30 ? 'critical' : thrashPct > 15 ? 'warning' : 'good', icon: mkIcon(faRotate), baseline: '✓ <15% | ⚠ 15–30% | ✗ >30%' });
          // Dev Rework Rate
          let reworkCount = 0;
          const issueLastForward = new Map<string, boolean>();
          const sortedEvts = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          for (const e of sortedEvts) {
            if (e.type === 'status_change' && e.toStatus) {
              if (isForwardStatus(e.toStatus)) {
                issueLastForward.set(e.issueKey, true);
              } else if (isReworkStatus(e.toStatus) && issueLastForward.get(e.issueKey)) {
                reworkCount++;
                issueLastForward.set(e.issueKey, false);
              }
            }
          }
          const uniqueIssueCount = new Set(events.map(e => e.issueKey)).size;
          const reworkPct = uniqueIssueCount > 0 ? (reworkCount / uniqueIssueCount) * 100 : 0;
          jiraLag.push({ id: 'jlag-rework', name: 'Dev Rework Rate', label: `${reworkCount} rework event${reworkCount !== 1 ? 's' : ''} (${reworkPct.toFixed(0)}% of issues)`, severity: reworkPct > 25 ? 'critical' : reworkPct > 10 ? 'warning' : 'good', icon: mkIcon(faFire), baseline: '✓ <10% | ⚠ 10–25% | ✗ >25%' });
          // Unassignment
          const unassign = events.filter(e => e.type === 'assignment' && !e.toAssignee).length;
          jiraLag.push({ id: 'jlag-unassign', name: 'Unassignment Events', label: `${unassign} unassignment${unassign !== 1 ? 's' : ''}`, severity: unassign > 10 ? 'critical' : unassign > 3 ? 'warning' : 'good', icon: mkIcon(faTriangleExclamation), baseline: '✓ ≤3 | ⚠ 4–10 | ✗ >10' });
          // Sprint incompletion
          if (aSprints.length > 0) {
            const tc2 = aSprints.reduce((s, v) => s + v.completedIssues, 0);
            const tt2 = aSprints.reduce((s, v) => s + v.totalIssues, 0);
            const ic = tt2 > 0 ? ((tt2 - tc2) / tt2) * 100 : 0;
            jiraLag.push({ id: 'jlag-sprint', name: 'Sprint Incompletion', label: `${ic.toFixed(0)}% incomplete`, severity: ic > 40 ? 'critical' : ic > 20 ? 'warning' : 'good', icon: mkIcon(faBullseye), baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%' });
          }
          // Stale Issues — issues with last activity >7 days ago (excluding done issues)
          const now = Date.now();
          const issueLastActivity = new Map<string, number>();
          const issueLastStatus = new Map<string, string>();
          for (const e of events) {
            const ts = new Date(e.timestamp).getTime();
            if (!issueLastActivity.has(e.issueKey) || ts > issueLastActivity.get(e.issueKey)!) {
              issueLastActivity.set(e.issueKey, ts);
            }
            if (e.type === 'status_change' && e.toStatus) {
              if (ts >= (issueLastActivity.get(e.issueKey) || 0)) issueLastStatus.set(e.issueKey, e.toStatus.toLowerCase());
            }
          }
          const openIssues = [...issueLastActivity.entries()].filter(([key]) => {
            const lastStatus = issueLastStatus.get(key) || '';
            return !isDoneStatus(lastStatus);
          });
          const staleIssues = openIssues.filter(([, ts]) => (now - ts) > 7 * 86400000);
          const stalePct = openIssues.length > 0 ? (staleIssues.length / openIssues.length) * 100 : 0;
          jiraLag.push({ id: 'jlag-stale', name: 'Stale Issues', label: `${staleIssues.length} issues (${stalePct.toFixed(0)}%)`, severity: stalePct > 40 ? 'critical' : stalePct > 20 ? 'warning' : 'good', icon: mkIcon(faClock), baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%' });

          // Merge overall-scoped server indicators into these tables
          const overallLag = indicators.filter(i => (i.scope || 'per-user') === 'overall');
          const overallLead = leadIndicators.filter(i => (i.scope || 'per-user') === 'overall');
          for (const ind of overallLag) {
            jiraLag.push({
              id: `server-lag-${ind.id}`,
              name: ind.name,
              label: ind.overallLabel || String(ind.overallValue),
              severity: ind.severity,
              icon: INDICATOR_ICONS[ind.id] || mkIcon(faChartBar),
              baseline: ind.description,
            });
          }
          for (const ind of overallLead) {
            jiraLead.push({
              id: `server-lead-${ind.id}`,
              name: ind.name,
              label: ind.overallLabel || String(ind.overallValue),
              severity: ind.severity,
              icon: INDICATOR_ICONS[ind.id] || mkIcon(faArrowTrendUp),
              baseline: ind.description,
            });
          }

          const renderAggTable = (title: string, icon: any, items: AggregateIndicator[], theme: 'lead' | 'lag') => {
            if (items.length === 0) return null;
            const Icon = icon;
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Icon className={`h-3 w-3 ${theme === 'lead' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} />
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{title}</span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700/30">
                  <table className="w-full text-[11px] table-fixed">
                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '8%' }} /><col style={{ width: '52%' }} /></colgroup>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900/50">
                        <th className="text-left px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Indicator</th>
                        <th className="text-center px-1 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400"></th>
                        <th className="text-right px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(ind => {
                        const colors = SEVERITY_COLORS[ind.severity] || SEVERITY_COLORS.warning;
                        const IndIcon = ind.icon;
                        return (
                          <tr key={ind.id} className="border-t border-gray-100 dark:border-slate-700/15 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                            <td className="px-2 py-1">
                              <div className="flex items-center gap-1.5">
                                <IndIcon className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                <span className="text-[11px] text-gray-700 dark:text-slate-300 truncate">{ind.name}</span>
                              </div>
                            </td>
                            <td className="px-1 py-1 text-center">
                              <span className={`inline-block w-5 h-5 leading-5 rounded text-[10px] font-bold text-center ${colors.bg} ${colors.text} border ${colors.border}`}>
                                {ind.severity === 'good' ? '✓' : ind.severity === 'warning' ? '⚠' : '✗'}
                              </span>
                            </td>
                            <td className="px-2 py-1 text-right">
                              <div className={`text-[10px] font-mono ${colors.text} truncate`}>{ind.label}</div>
                              <div className="text-[8px] font-mono text-slate-600 truncate">{ind.baseline}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          };

          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              {renderAggTable('Overall Jira Lead Indicators', mkIcon(faArrowTrendUp), jiraLead, 'lead')}
              {renderAggTable('Overall Jira Lag Indicators', mkIcon(faArrowTrendDown), jiraLag, 'lag')}
            </div>
          );
        })()}
      </div>
    );
  }

  // Full standalone mode
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-slate-400" />
            </button>
            <FontAwesomeIcon icon={faChartBar} className="h-5 w-5 text-blue-400" />
            <div>
              <h1 className="text-lg font-semibold text-white">Lag Indicators</h1>
              <p className="text-xs text-slate-400">Developer quality metrics from Jira</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastCalculated && (
              <span className="text-xs text-slate-500">
                Last calculated: {lastCalculated.toLocaleTimeString()}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <FontAwesomeIcon icon={faXmark} className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Configuration Panel */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Project Select */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5" />
                Project
              </label>
              {loadingProjects ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-2.5">
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                  Loading...
                </div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-700/30 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                >
                  <option value="">Select project...</option>
                  {projects.map((p) => (
                    <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
                  ))}
                </select>
              )}
            </div>

            {/* Lookback Days */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" />
                Lookback
              </label>
              <select
                value={lookbackDays}
                onChange={(e) => setLookbackDays(Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-slate-700/30 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>

            {/* Calculate Button */}
            <div className="flex items-end">
              <button
                onClick={calculateIndicators}
                disabled={loading || !selectedProject}
                className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBolt} className="h-4 w-4" />
                    Calculate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* ===================== PER-USER MATRIX TABLE (Andrew's format) ===================== */}
        {indicators.length > 0 && (() => {
          // Build unique developer list from all indicator results, sorted by total activity
          const devActivity = new Map<string, number>();
          for (const ind of indicators) {
            for (const r of ind.results) {
              devActivity.set(r.developerName, (devActivity.get(r.developerName) || 0) + Math.abs(r.value));
            }
          }
          const allDevNamesSorted = Array.from(devActivity.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);

          // Priority users first (in order), then the rest sorted by activity
          const prioritySet = new Set(priorityUsers.map(n => n.toLowerCase()));
          const priorityMatched = priorityUsers
            .map(pu => allDevNamesSorted.find(d => d.toLowerCase() === pu.toLowerCase()))
            .filter(Boolean) as string[];
          const rest = allDevNamesSorted.filter(d => !prioritySet.has(d.toLowerCase()));
          const allDevNames = [...priorityMatched, ...rest];

          const MAX_VISIBLE = 6;
          const visibleDevs = showAllUsers ? allDevNames : allDevNames.slice(0, MAX_VISIBLE);
          const hiddenCount = allDevNames.length - MAX_VISIBLE;

          // Helper to get result for a dev from an indicator
          const getDevResult = (indicator: LagIndicatorResult, devName: string): LagIndicatorDevResult | undefined =>
            indicator.results.find((r) => r.developerName === devName);

          return (
            <div className="space-y-6">
              {/* Collapsible header */}
              <button
                onClick={() => setIndicatorsCollapsed(!indicatorsCollapsed)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:bg-slate-800/70 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faChartBar} className="h-4 w-4 text-blue-400" />
                  <span className="text-sm font-semibold text-white">Per-User Indicators</span>
                  <div className="flex items-center gap-1.5 ml-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">{goodCount} ✓</span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">{warningCount} ⚠</span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20">{criticalCount} ✗</span>
                  </div>
                </div>
                {indicatorsCollapsed ? <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-slate-400" /> : <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-slate-400" />}
              </button>

              {!indicatorsCollapsed && (
              <>
              {/* Summary row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{goodCount}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-300/70 mt-1">Healthy</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{warningCount}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-300/70 mt-1">Needs Attention</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-700 dark:text-red-400">{criticalCount}</p>
                  <p className="text-xs text-red-600 dark:text-red-300/70 mt-1">Critical</p>
                </div>
              </div>

              {/* Main Matrix Table */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-900/50 border-b border-slate-700/30">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-300 w-10">ID</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-300 min-w-[160px]">Lag Indicator Name</th>
                        {visibleDevs.map((name) => (
                          <th key={name} className="text-center px-3 py-3 text-xs font-semibold text-slate-300 capitalize min-w-[80px]">
                            {name.split(' ')[0]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {indicators.map((indicator) => {
                        const isExpanded = expandedIndicators.has(indicator.id);
                        const Icon = INDICATOR_ICONS[indicator.id] || mkIcon(faChartBar);
                        return (
                          <>
                            <tr
                              key={indicator.id}
                              className="border-b border-slate-700/20 hover:bg-slate-700/10 transition-colors cursor-pointer"
                              onClick={() => toggleIndicator(indicator.id)}
                            >
                              <td className="px-4 py-3 text-xs text-slate-500 font-mono">{indicator.id}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  <span className="text-xs text-white font-medium">{indicator.name}</span>
                                  {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-slate-500" />}
                                </div>
                              </td>
                              {visibleDevs.map((name) => {
                                const result = getDevResult(indicator, name);
                                if (!result) return <td key={name} className="px-3 py-3 text-center text-xs text-slate-600">—</td>;
                                const colors = SEVERITY_COLORS[result.severity];
                                return (
                                  <td key={name} className="px-3 py-3 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                                      {result.label}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                            {/* Expanded detail row */}
                            {isExpanded && (
                              <tr key={`${indicator.id}-detail`} className="bg-slate-900/30">
                                <td colSpan={2 + visibleDevs.length} className="px-4 py-3">
                                  <div className="text-xs text-slate-400 mb-2">{indicator.description}</div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {indicator.results.map((dev) => {
                                      const devColors = SEVERITY_COLORS[dev.severity];
                                      return (
                                        <div key={dev.developerName} className={`p-2.5 rounded-lg border ${devColors.border} ${devColors.bg}`}>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-white font-medium capitalize">{dev.developerName}</span>
                                            <span className={`text-xs font-bold ${devColors.text}`}>{dev.label}</span>
                                          </div>
                                          <div className="text-[10px] text-slate-400">{dev.details}</div>
                                          {dev.issueKeys && dev.issueKeys.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                              {dev.issueKeys.slice(0, 5).map((key) => (
                                                <a key={key} href={`https://${jiraDomain}.atlassian.net/browse/${key}`}
                                                  target="_blank" rel="noopener noreferrer"
                                                  className="text-[10px] px-1.5 py-0.5 bg-slate-700/50 text-blue-400 hover:text-blue-300 rounded font-mono transition-colors">
                                                  {key}
                                                </a>
                                              ))}
                                              {dev.issueKeys.length > 5 && (
                                                <span className="text-[10px] text-slate-500">+{dev.issueKeys.length - 5} more</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Show More / Less toggle */}
                {hiddenCount > 0 && (
                  <div className="border-t border-slate-700/30 px-4 py-2.5 flex items-center justify-center">
                    <button
                      onClick={() => setShowAllUsers(!showAllUsers)}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                    >
                      {showAllUsers ? (
                        <>Show fewer users <FontAwesomeIcon icon={faChevronUp} className="h-3 w-3" /></>
                      ) : (
                        <>Show {hiddenCount} more user{hiddenCount !== 1 ? 's' : ''} <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /></>
                      )}
                    </button>
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          );
        })()}

        {/* Empty state */}
        {!loading && indicators.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faChartBar} className="h-8 w-8 text-slate-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-300 mb-2">No indicators yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Select a project and click Calculate to generate lag indicators for all team members.
              All Jira users on the project are automatically discovered.
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-16">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">Calculating indicators...</h3>
            <p className="text-sm text-slate-500">
              Fetching issues and analyzing transitions. This may take a minute for large projects.
            </p>
          </div>
        )}

        {/* Indicator Reference */}
        {indicators.length === 0 && !loading && (
          <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-5">
            <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Indicator Reference</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { id: 1, name: '% Ready for Testing → DEV Rework', desc: 'Cards sent back from QA Testing/Release Testing to DEV Rework' },
                { id: 2, name: '% Ready for Testing → Release Candidate', desc: 'Cards that passed testing and moved to Release Candidate' },
                { id: 3, name: 'Bugs within 30 days', desc: 'Post-completion bugs raised against a component by that dev' },
                { id: 4, name: 'Estimate overrun rate', desc: '(# cards exceeding estimate by >100%) ÷ (total completed)' },
                { id: 5, name: 'Cards without commit', desc: '# cards completed without an attached commit' },
                { id: 6, name: 'Hotfix bugs without commit', desc: '# hotfix bugs marked Complete without an attached commit' },
                { id: 7, name: 'Average cycle time', desc: 'In Progress → Completed/Live in Production duration' },
                { id: 8, name: 'Sprint scope creep', desc: 'Cards added after sprint start' },
                { id: 9, name: 'Reopened issues rate', desc: 'Issues sent back to In Progress/DEV Rework' },
              ].map((item) => {
                const Icon = INDICATOR_ICONS[item.id] || mkIcon(faChartBar);
                return (
                  <div key={item.id} className="flex items-start gap-2.5 p-3 bg-slate-800/50 rounded-lg">
                    <Icon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-slate-300">#{item.id} {item.name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Token Expiry Modal */}
      <TokenExpiryModal />
    </div>
  );
}

export default JiraLagIndicators;
